import { auth } from './lib/firebase';
import {
  applyActionCode,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  User,
  updateProfile,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from 'firebase/auth';
import {
  getDashboardPermissions,
  getUserProfile,
} from './dataProvider/dataProvider';
import { logEvent } from './analytics';
import { AuthProvider, fetchUtils, UserIdentity } from 'react-admin';
import { canAccessWithPermissions } from './rbac/canAccessWithPermissions';
import { API_URL } from './constants';

export interface ExtendedAuthProvider extends AuthProvider {
  getAccessToken: () => Promise<string | undefined>;
  setBrowserLocalPersistence: () => Promise<void>;
  verifyEmail: (oobCode: string) => Promise<'success'>;
  login: (arg0: {
    email?: string;
    password?: string;
    provider: 'emailAndPassword' | 'google';
    isSignup: boolean;
  }) => ReturnType<AuthProvider['login']>;
  getCurrentUser: () => User | null;
  updateUserProfile: (payload: {
    displayName?: string | null;
    photoURL?: string | null;
  }) => Promise<void>;
  sendPasswordResetLink: (payload: {
    email: string;
    token: string;
    action: string;
  }) => Promise<void>;
  verifyPasswordResetCode: (oobCode: string) => Promise<string>;
  confirmPasswordReset: (oobCode: string, newPassword: string) => Promise<void>;
  sendEmailVerification: () => Promise<void>;
}

export const authProvider: ExtendedAuthProvider = {
  login: async ({ email = '', password = '', provider, isSignup }) => {
    let userCredential;

    switch (provider) {
      case 'emailAndPassword':
        if (isSignup) {
          userCredential = await createUserWithEmailAndPassword(
            auth,
            email,
            password
          );
        } else {
          userCredential = await signInWithEmailAndPassword(
            auth,
            email,
            password
          );
        }
        break;
      case 'google':
        userCredential = await signInWithPopup(auth, new GoogleAuthProvider());
        break;
    }

    if (!userCredential) {
      return Promise.reject('Authentication failed');
    }

    logEvent(isSignup ? 'sign-up' : 'login', {
      provider,
      emailVerified: userCredential.user?.emailVerified,
    });

    if (!userCredential.user?.emailVerified) {
      await sendEmailVerification(userCredential.user);
      logEvent('email-verification-sent');
      return Promise.reject({ hasToVerifyEmail: true });
    }

    const permissions = await getDashboardPermissions();
    localStorage.setItem('permissions', JSON.stringify(permissions));

    const userProfile = await getUserProfile();
    if (userProfile) {
      const firstPublisher = userProfile.publishers[0];
      if (firstPublisher) {
        return Promise.resolve({
          redirectTo: `/publisher/${firstPublisher.id}/offers`,
        });
      }
      return Promise.reject({ redirectTo: `/login`, code: 'no_publishers' });
    }
    return Promise.resolve({ redirectTo: '/complete-profile' });
  },

  logout: async () => {
    await auth.signOut();
    localStorage.removeItem('permissions');
    await signOut(auth);
  },

  checkAuth: () => {
    return auth.currentUser
      ? Promise.resolve()
      : Promise.reject({ message: false });
  },

  checkError: async error => {
    console.error('check error', error);
    const status = error.status;
    if (status === 401 || status === 403) {
      await auth.signOut();
      return Promise.reject();
    }
    return Promise.resolve();
  },

  getIdentity: () => {
    if (auth.currentUser) {
      return Promise.resolve({
        id: auth.currentUser.uid,
        fullName: auth.currentUser.displayName,
        avatar: auth.currentUser.photoURL,
        emailVerified: auth.currentUser.emailVerified,
      } as UserIdentity);
    }
    return Promise.reject('No user found');
  },

  getPermissions: () => {
    const permissions = localStorage.getItem('permissions');
    return permissions
      ? Promise.resolve(JSON.parse(permissions))
      : Promise.resolve([]);
  },

  canAccess: ({ resource, action }) => {
    const storedData = localStorage.getItem('permissions');

    const permissions = storedData ? JSON.parse(storedData) : [];
    return Promise.resolve(
      canAccessWithPermissions({ permissions, action, resource })
    );
  },

  verifyEmail: async oobCode => {
    await applyActionCode(auth, oobCode);
    logEvent('email-verified');
    return Promise.resolve('success');
  },
  getAccessToken: async () => {
    return auth.currentUser?.getIdToken();
  },
  setBrowserLocalPersistence: async () => {
    await auth.setPersistence(browserLocalPersistence);
  },
  getCurrentUser: () => {
    return auth.currentUser;
  },
  updateUserProfile: async payload => {
    if (auth.currentUser) {
      return updateProfile(auth.currentUser, payload);
    }
    return Promise.reject('No user found');
  },
  sendPasswordResetLink: async payload => {
    await fetchUtils.fetchJson(
      `${API_URL}/publisher-users/send-password-reset-link`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
    logEvent('password-reset-email-sent', { email: payload.email });

    return Promise.resolve();
  },
  verifyPasswordResetCode: async code => {
    return verifyPasswordResetCode(auth, code);
  },
  confirmPasswordReset: async (oobCode, newPassword) => {
    return confirmPasswordReset(auth, oobCode, newPassword);
  },
  sendEmailVerification: async () => {
    if (!auth.currentUser) {
      throw new Error('No user found');
    }
    return sendEmailVerification(auth.currentUser);
  },
};
